// Componente OnboardingWizard com todos os steps
import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Stepper } from './Stepper';
import { WelcomeStep } from './WelcomeStep';
import { CompanyDataStep } from './CompanyDataStep';
import { AddressStep } from './AddressStep';
import { ConfirmationStep } from './ConfirmationStep';
import { SuccessStep } from './SuccessStep';
import { Loading } from '../common/Loading';
import { supabase } from '../../lib/supabase';
import { vincularUsuarioEmpresa } from '../../services/usuariosService';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';

interface WizardData {
  companyData: {
    razaoSocial: string;
    nomeFantasia: string;
    cnpj: string;
    inscricaoEstadual: string;
    email: string;
    telefone: string;
  };
  addressData: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
  };
}

const STEPS = [
  { label: 'Boas-vindas', key: 'welcome' },
  { label: 'Dados da Empresa', key: 'company' },
  { label: 'Endereço', key: 'address' },
  { label: 'Confirmação', key: 'confirmation' },
  { label: 'Sucesso', key: 'success' },
];

interface OnboardingWizardProps {
  isOpen: boolean;
  onComplete: () => void;
}

export function OnboardingWizard({ isOpen, onComplete }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wizardData, setWizardData] = useState<WizardData>({
    companyData: {
      razaoSocial: '',
      nomeFantasia: '',
      cnpj: '',
      inscricaoEstadual: '',
      email: '',
      telefone: '',
    },
    addressData: {
      cep: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
    },
  });

  const { user } = useAuth();
  const { setEmpresaSelecionada, adicionarEmpresa } = useEmpresa();

  // Carregar dados salvos do localStorage
  useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem('nfsis_onboarding_data');
      if (saved) {
        try {
          setWizardData(JSON.parse(saved));
        } catch (error) {
          console.error('Erro ao carregar dados salvos:', error);
        }
      }
    }
  }, [isOpen]);

  // Salvar dados no localStorage
  useEffect(() => {
    if (isOpen && currentStep > 0 && currentStep < STEPS.length - 1) {
      localStorage.setItem('nfsis_onboarding_data', JSON.stringify(wizardData));
    }
  }, [wizardData, currentStep, isOpen]);

  // Limpar dados após conclusão
  const clearSavedData = () => {
    localStorage.removeItem('nfsis_onboarding_data');
    localStorage.setItem('nfsis_first_time_completed', 'true');
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCompanyDataChange = (data: Partial<WizardData['companyData']>) => {
    setWizardData(prev => ({
      ...prev,
      companyData: { ...prev.companyData, ...data },
    }));
  };

  const handleAddressDataChange = (data: Partial<WizardData['addressData']>) => {
    setWizardData(prev => ({
      ...prev,
      addressData: { ...prev.addressData, ...data },
    }));
  };

  const handleFinalize = async () => {
    if (!user) {
      setError('Usuário não autenticado');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Criar endereço
      const { data: endereco, error: enderecoError } = await supabase
        .from('enderecos')
        .insert({
          cep: wizardData.addressData.cep.replace(/\D/g, ''),
          logradouro: wizardData.addressData.logradouro,
          numero: wizardData.addressData.numero,
          complemento: wizardData.addressData.complemento || null,
          bairro: wizardData.addressData.bairro,
          cidade: wizardData.addressData.cidade,
          estado: wizardData.addressData.estado,
        })
        .select()
        .single();

      if (enderecoError) throw enderecoError;
      if (!endereco) throw new Error('Erro ao criar endereço');

      // Criar empresa usando função RPC
      const cnpjLimpo = wizardData.companyData.cnpj.replace(/\D/g, '');
      const { data: empresa, error: empresaError } = await supabase.rpc('criar_empresa_teste', {
        p_razao_social: wizardData.companyData.razaoSocial,
        p_nome_fantasia: wizardData.companyData.nomeFantasia,
        p_cnpj: cnpjLimpo,
        p_inscricao_estadual: wizardData.companyData.inscricaoEstadual || null,
        p_email: wizardData.companyData.email,
        p_telefone: wizardData.companyData.telefone.replace(/\D/g, ''),
        p_usuario_id: user.id,
        p_endereco_id: endereco.id,
      });

      if (empresaError) throw empresaError;
      if (!empresa) throw new Error('Erro ao criar empresa');

      // Buscar empresa completa
      const { data: empresaCompleta, error: empresaCompletaError } = await supabase
        .from('empresas')
        .select('*, enderecos(*)')
        .eq('id', empresa)
        .single();

      if (empresaCompletaError) throw empresaCompletaError;
      if (!empresaCompleta) throw new Error('Empresa não encontrada');

      // Vincular usuário à empresa
      await vincularUsuarioEmpresa(user.id, empresa, 'proprietario');

      // Construir objeto Empresa
      const empresaObj = {
        id: empresaCompleta.id,
        razaoSocial: empresaCompleta.razao_social,
        nomeFantasia: empresaCompleta.nome_fantasia,
        cnpj: empresaCompleta.cnpj,
        inscricaoEstadual: empresaCompleta.inscricao_estadual || '',
        email: empresaCompleta.email,
        telefone: empresaCompleta.telefone,
        endereco: empresaCompleta.enderecos ? {
          id: empresaCompleta.enderecos.id,
          cep: empresaCompleta.enderecos.cep,
          logradouro: empresaCompleta.enderecos.logradouro,
          numero: empresaCompleta.enderecos.numero,
          complemento: empresaCompleta.enderecos.complemento || '',
          bairro: empresaCompleta.enderecos.bairro,
          cidade: empresaCompleta.enderecos.cidade,
          estado: empresaCompleta.enderecos.estado,
        } : undefined,
        contaId: empresaCompleta.conta_id || null,
      };

      // Adicionar ao contexto
      adicionarEmpresa(empresaObj as any);
      setEmpresaSelecionada(empresaObj as any);

      // Avançar para step de sucesso
      setCurrentStep(STEPS.length - 1);
      clearSavedData();
    } catch (error: any) {
      console.error('Erro ao criar empresa:', error);
      setError(error.message || 'Erro ao criar empresa. Tente novamente.');
      setCurrentStep(2); // Voltar para step de endereço
    } finally {
      setIsSaving(false);
    }
  };

  const handleComplete = () => {
    clearSavedData();
    setCurrentStep(0);
    setWizardData({
      companyData: {
        razaoSocial: '',
        nomeFantasia: '',
        cnpj: '',
        inscricaoEstadual: '',
        email: '',
        telefone: '',
      },
      addressData: {
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
      },
    });
    onComplete();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;
      case 1:
        return (
          <CompanyDataStep
            data={wizardData.companyData}
            onChange={handleCompanyDataChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <AddressStep
            data={wizardData.addressData}
            onChange={handleAddressDataChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <ConfirmationStep
            companyData={wizardData.companyData}
            addressData={wizardData.addressData}
            onNext={handleFinalize}
            onBack={handleBack}
          />
        );
      case 4:
        return <SuccessStep onComplete={handleComplete} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Não permitir fechar durante onboarding
      title={currentStep > 0 && currentStep < STEPS.length - 1 ? 'Configuração Inicial' : undefined}
      size="xl"
      showCloseButton={currentStep === 4} // Só mostrar botão de fechar no último step
      closeOnBackdropClick={currentStep === 4} // Só permitir fechar no último step
    >
      {isSaving ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loading />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Criando empresa...
          </p>
        </div>
      ) : (
        <>
          {currentStep > 0 && currentStep < STEPS.length - 1 && (
            <div className="mb-8">
              <Stepper steps={STEPS} currentStep={currentStep} />
            </div>
          )}

          {error && currentStep !== 4 && (
            <div className="mb-6 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl">
              <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
            </div>
          )}

          <div className="min-h-[400px]">{renderStep()}</div>
        </>
      )}
    </Modal>
  );
}
